import type { User } from 'firebase/auth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { analyticsClient } from '@/lib';
import {
  countLikesByPhoto,
  filterBlockedLikes,
  filterOrphanLikes,
  likeService,
  type PhotoLike,
} from '@/services';
import type { ProfilePhoto } from '@/types/models';
import { useBlocks } from './useBlocks';

const EMPTY_LIKES: PhotoLike[] = [];
const EMPTY_SET: Set<string> = new Set();

/**
 * The signed-in user's like state on another member's profile: which of their
 * photos are already liked, plus a `toggleLike` to like/unlike one.
 */
export function useSentLikes(user: User | null, targetUid: string | undefined) {
  const queryClient = useQueryClient();
  const { blockedUids, blockedByUids, isLoading: blocksLoading } = useBlocks(user);
  const [error, setError] = useState<Error | null>(null);

  const { data } = useQuery<Set<string>>({
    queryKey: ['profileLikes', user?.uid, targetUid],
    // placeholder only; subscription fills the cache
    queryFn: () => EMPTY_SET,
    enabled: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!user || !targetUid || user.uid === targetUid) return;

    const unsubscribe = likeService.subscribeToMyLikesOnProfile(
      user.uid,
      targetUid,
      photoIds => {
        setError(null);
        queryClient.setQueryData(['profileLikes', user.uid, targetUid], photoIds);
      },
      setError
    );

    return () => unsubscribe();
  }, [user, targetUid, queryClient]);

  const isBlocked = Boolean(
    targetUid && (blockedUids.includes(targetUid) || blockedByUids.includes(targetUid))
  );

  const likedPhotoIds =
    user && targetUid && user.uid !== targetUid ? (data ?? EMPTY_SET) : EMPTY_SET;

  const isLiked = useCallback((photoId: string) => likedPhotoIds.has(photoId), [likedPhotoIds]);

  // Ref guards in-flight writes synchronously; state mirrors it for the UI.
  const pendingRef = useRef(new Set<string>());
  const [pendingIds, setPendingIds] = useState<Set<string>>(EMPTY_SET);

  const isPending = useCallback((photoId: string) => pendingIds.has(photoId), [pendingIds]);

  const toggleLike = useCallback(
    async (photo: ProfilePhoto) => {
      if (!user || !targetUid || isBlocked || pendingRef.current.has(photo.id)) return;
      const key = ['profileLikes', user.uid, targetUid];
      const wasLiked = likedPhotoIds.has(photo.id);
      const withLiked = (old: Set<string> | undefined, liked: boolean) => {
        const next = new Set(old);
        if (liked) next.add(photo.id);
        else next.delete(photo.id);
        return next;
      };
      pendingRef.current.add(photo.id);
      setPendingIds(new Set(pendingRef.current));
      // Optimistic flip; the subscription snapshot reconciles on ack.
      queryClient.setQueryData<Set<string>>(key, old => withLiked(old, !wasLiked));
      try {
        if (wasLiked) {
          await likeService.unlikePhoto(user.uid, targetUid, photo.id);
        } else {
          await likeService.likePhoto(user.uid, targetUid, photo);
        }
        analyticsClient.capture(wasLiked ? 'photo_unliked' : 'photo_liked', {
          photo_id: photo.id,
          liker_id: user.uid,
          target_id: targetUid,
          session_id: analyticsClient.getCurrentSessionId(),
        });
      } catch (err) {
        queryClient.setQueryData<Set<string>>(key, old => withLiked(old, wasLiked));
        console.error('Photo like toggle failure:', err);
      } finally {
        pendingRef.current.delete(photo.id);
        setPendingIds(new Set(pendingRef.current));
      }
    },
    [user, targetUid, isBlocked, likedPhotoIds, queryClient]
  );

  return {
    likedPhotoIds,
    isLiked,
    isPending,
    toggleLike,
    isBlocked,
    isLoading: blocksLoading,
    isError: error != null,
    error,
  };
}

/**
 * Likes received on the signed-in user's own photos, newest first. Likes
 * involving blocked members and likes on photos that no longer exist are
 * filtered out.
 */
export function useReceivedLikes(user: User | null, myPhotos: ProfilePhoto[]) {
  const queryClient = useQueryClient();
  const { blockedUids, blockedByUids } = useBlocks(user);
  const [retryKey, setRetryKey] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  const { data } = useQuery<PhotoLike[]>({
    queryKey: ['receivedLikes', user?.uid],
    // placeholder only; subscription fills the cache
    queryFn: () => EMPTY_LIKES,
    enabled: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!user) return;

    const unsubscribe = likeService.subscribeToReceivedLikes(
      user.uid,
      likes => {
        setError(null);
        queryClient.setQueryData(['receivedLikes', user.uid], likes);
      },
      setError
    );

    return () => unsubscribe();
  }, [user, queryClient, retryKey]);

  const photoIds = useMemo(() => new Set(myPhotos.map(photo => photo.id)), [myPhotos]);

  const likes = useMemo(() => {
    const visible = filterBlockedLikes(data ?? EMPTY_LIKES, [...blockedUids, ...blockedByUids]);
    return filterOrphanLikes(visible, photoIds);
  }, [data, blockedUids, blockedByUids, photoIds]);

  const likeCountByPhotoId = useMemo(() => countLikesByPhoto(likes), [likes]);

  const refetch = useCallback(() => {
    setError(null);
    setRetryKey(prev => prev + 1);
  }, []);

  return {
    likes,
    likeCountByPhotoId,
    isLoading: Boolean(user && data === undefined && !error),
    isError: error != null,
    error,
    refetch,
  };
}
